/**
 * Login Page for HMH Lunch Ordering System
 *
 * Provides SSO login button and guest login option with ticket redemption.
 */

import { useState } from 'react';
import { useAuth, TicketGuestLogin } from '../components/auth';

export function LoginPage() {
  const { login, isLoading } = useAuth();
  const [showGuestForm, setShowGuestForm] = useState(false);
  const [ssoError, setSsoError] = useState<string | null>(null);

  const handleSsoLogin = async () => {
    try {
      setSsoError(null);
      await login();
    } catch (error) {
      console.error('SSO login error:', error);
      setSsoError('Sign in failed. Please try again or use guest login.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-hmh-dark to-hmh-blue flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <img 
              src="https://hmhintuneassets.blob.core.windows.net/iconstorage/HMH-Logo-White.png?sv=2025-07-05&spr=https&st=2025-12-31T19%3A54%3A31Z&se=2054-12-01T19%3A54%3A00Z&sr=b&sp=r&sig=fkkN5B5HXMcBBnQ%2FqWaZukN%2FoE6CaeuguUNs73YKavo%3D"
              alt="Hood Memorial Hospital"
              className="h-20 w-auto"
            />
          </div>
          <h2 className="text-xl text-white font-medium">Lunch Ordering System</h2>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-xl shadow-2xl p-8">
          {!showGuestForm ? (
            <>
              {/* SSO Login */}
              <div className="mb-6">
                <button
                  onClick={handleSsoLogin}
                  disabled={isLoading}
                  className="w-full py-3 px-4 bg-hmh-blue text-white font-medium rounded-lg hover:bg-hmh-dark focus:outline-none focus:ring-2 focus:ring-hmh-blue focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <svg
                        className="animate-spin h-5 w-5"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Signing In...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-5 h-5"
                        viewBox="0 0 21 21"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                        <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                        <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                        <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                      </svg>
                      Sign in with Microsoft
                    </>
                  )}
                </button>

                {ssoError && (
                  <p className="mt-2 text-sm text-red-600 text-center">
                    {ssoError}
                  </p>
                )}
              </div>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">
                    Or continue as guest
                  </span>
                </div>
              </div>

              {/* Guest Login Button */}
              <button
                onClick={() => setShowGuestForm(true)}
                className="w-full py-3 px-4 border-2 border-hmh-blue text-hmh-blue font-medium rounded-lg hover:bg-hmh-blue hover:text-white focus:outline-none focus:ring-2 focus:ring-hmh-blue focus:ring-offset-2 transition-colors"
              >
                Guest Login with Ticket
              </button>
            </>
          ) : (
            <>
              {/* Back Button */}
              <button
                onClick={() => setShowGuestForm(false)}
                className="mb-4 text-hmh-blue hover:text-hmh-dark flex items-center gap-1 font-medium"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                Back to SSO Login
              </button>

              {/* Guest Ticket Login */}
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Guest Login with Ticket
              </h3>
              <TicketGuestLogin />
            </>
          )}
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-sm text-white">
          Hood Memorial Hospital - Amite, Louisiana
        </p>
      </div>
    </div>
  );
}
